/**
 * Demo-grade Q&A over the federation rollup, vulnerability bands,
 * outcome calibration, and external benchmarks. Internal CBO only —
 * the endpoint returns 403 for non-internal callers.
 *
 * Powered by the /qa endpoint which assembles a structured "evidence
 * pack" from materialized views and asks Claude for narrative phrasing
 * with citations. All quoted numbers come from SQL, not the LLM.
 */

import React, { useState } from 'react';
import { Sparkles, Send, Loader2, BookOpen, MessageCircle } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { useLegacyTrustQA } from '@/hooks/ml';

const SUGGESTED_QUESTIONS = [
  'How did the CCHI federation perform overall on Medi-Cal enrollment outreach?',
  'How does our vulnerability profile compare to published Medicaid benchmarks?',
  'Which vulnerability band drove the bulk of projected enrollments and why?',
  'What is the realistic outcome conversion from our SMS replies and clicks?',
];

export const AnalyticsQACard: React.FC = () => {
  const [question, setQuestion] = useState('');
  const qa = useLegacyTrustQA();

  const submit = (q: string) => {
    const trimmed = q.trim();
    if (!trimmed || qa.isPending) return;
    setQuestion(trimmed);
    qa.mutate({ question: trimmed });
  };

  return (
    <Card>
      <CardContent className="pt-4 space-y-3">
        <div className="flex items-start gap-2 text-xs text-gray-600">
          <Sparkles className="h-3.5 w-3.5 flex-shrink-0 mt-0.5 text-violet-500" />
          <span>
            Ask any question about the legacy outreach data. Answers cite the
            specific materialized view each number came from — all metrics are
            deterministic; only the phrasing is LLM-generated.
          </span>
        </div>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            submit(question);
          }}
          className="flex gap-2"
        >
          <input
            type="text"
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            placeholder="e.g. How did CCHI reach older monolingual Spanish speakers in Kern County?"
            className="flex-1 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:border-violet-400 focus:outline-none focus:ring-1 focus:ring-violet-400"
            disabled={qa.isPending}
          />
          <button
            type="submit"
            disabled={qa.isPending || !question.trim()}
            className="inline-flex items-center gap-1.5 rounded-md bg-violet-600 px-3 py-2 text-sm font-medium text-white hover:bg-violet-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {qa.isPending ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Send className="h-3.5 w-3.5" />
            )}
            Ask
          </button>
        </form>

        {!qa.data && !qa.isPending && (
          <div className="space-y-1.5">
            <div className="text-xs font-medium text-gray-500">Suggested questions:</div>
            <div className="flex flex-wrap gap-1.5">
              {SUGGESTED_QUESTIONS.map((q) => (
                <button
                  key={q}
                  type="button"
                  onClick={() => submit(q)}
                  className="rounded-full border border-gray-200 bg-gray-50 px-3 py-1 text-xs text-gray-700 hover:border-violet-300 hover:bg-violet-50"
                >
                  {q}
                </button>
              ))}
            </div>
          </div>
        )}

        {qa.isPending && (
          <div className="flex items-center gap-2 rounded-md border border-violet-200 bg-violet-50 p-3 text-sm text-violet-900">
            <Loader2 className="h-4 w-4 animate-spin" />
            Assembling evidence pack and phrasing answer…
          </div>
        )}

        {qa.isError && (
          <div className="rounded-md border border-rose-200 bg-rose-50 p-3 text-sm text-rose-900">
            QA request failed: {(qa.error as Error)?.message ?? 'unknown error'}
          </div>
        )}

        {qa.data?.error && (
          <div className="rounded-md border border-rose-200 bg-rose-50 p-3 text-sm text-rose-900">
            {qa.data.answer}
          </div>
        )}

        {qa.data && !qa.data.error && (
          <div className="space-y-2 rounded-md border border-gray-200 bg-white p-3">
            <div className="whitespace-pre-wrap text-sm text-gray-800 leading-relaxed">
              {qa.data.answer}
            </div>
            {qa.data.citations.length > 0 && (
              <div className="flex flex-wrap items-center gap-1.5 border-t border-gray-100 pt-2">
                <BookOpen className="h-3 w-3 text-gray-400" />
                <span className="text-xs text-gray-500">Sources:</span>
                {qa.data.citations.map((c) => (
                  <code
                    key={c}
                    className="rounded bg-gray-100 px-1.5 py-0.5 text-[10px] font-mono text-gray-700"
                  >
                    {c}
                  </code>
                ))}
              </div>
            )}
            {qa.data.retrieved_chunks && qa.data.retrieved_chunks.length > 0 && (
              <div className="border-t border-gray-100 pt-2 space-y-1">
                <div className="flex items-center gap-1 text-[10px] text-gray-500">
                  <MessageCircle className="h-3 w-3" />
                  Top retrieved corpus chunks (semantic match):
                </div>
                <ul className="space-y-1">
                  {qa.data.retrieved_chunks.slice(0, 5).map((c, i) => (
                    <li
                      key={i}
                      className="rounded border border-gray-100 bg-gray-50 px-2 py-1 text-[11px]"
                    >
                      <div className="flex items-center gap-1.5 text-[10px] text-gray-500 mb-0.5">
                        <span className="rounded bg-gray-200 px-1.5 py-0.5 font-mono">
                          {c.source_type}
                        </span>
                        {c.federation_code && (
                          <span className="rounded bg-indigo-100 px-1.5 py-0.5 text-indigo-700">
                            {c.federation_code}
                          </span>
                        )}
                        {c.intent_label && (
                          <span className="rounded bg-emerald-100 px-1.5 py-0.5 text-emerald-700">
                            {c.intent_label}
                          </span>
                        )}
                        {c.language && (
                          <span className="rounded bg-amber-100 px-1.5 py-0.5 text-amber-700">
                            {c.language}
                          </span>
                        )}
                        <span className="ml-auto text-gray-400">
                          sim {(c.similarity * 100).toFixed(0)}%
                        </span>
                      </div>
                      <div className="text-gray-700 break-words">"{c.preview}"</div>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <div className="text-[10px] text-gray-400">
              Phrasing model: {qa.data.model}
              {qa.data.retrieved_chunks?.length
                ? ` · ${qa.data.retrieved_chunks.length} vector chunks retrieved`
                : ''}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AnalyticsQACard;
